import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Bluetooth, BluetoothConnected, BluetoothSearching, HeartPulse, Activity,
  Thermometer, Droplet, RefreshCw, CheckCircle2, XCircle, Loader2, BatteryFull,
  ChevronRight, FlaskConical, Cpu, Save,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import {
  bridgeMode, scanForDevices, connectDevice, disconnectDevice, startMeasure,
} from "@/lib/linktopBridge";

/* ============================================================================
 * Per-vital capture configuration (one dedicated window per vital)
 * ==========================================================================*/
const DEVICE_VITALS = [
  {
    key: "bp", label: "Blood Pressure", measure: "bp", icon: HeartPulse, color: "#DC2626",
    instruction: "Wrap the cuff around the arm and stay still.", wave: false,
    summary: (v) => [`${v.bp_systolic}/${v.bp_diastolic}`, "mmHg", `HR ${v.heart_rate} bpm`],
    readings: (v) => [
      { vital_type: "bp_systolic", value: v.bp_systolic, unit: "mmHg" },
      { vital_type: "bp_diastolic", value: v.bp_diastolic, unit: "mmHg" },
      { vital_type: "heart_rate", value: v.heart_rate, unit: "bpm" },
    ],
  },
  {
    key: "spo2", label: "Blood Oxygen (SpO₂)", measure: "spo2", icon: Activity, color: "#0B7E8C",
    instruction: "Rest the fingertip on the optical sensor.", wave: true,
    summary: (v) => [`${v.spo2}`, "%", `HR ${v.heart_rate} bpm`],
    readings: (v) => [
      { vital_type: "spo2", value: v.spo2, unit: "%" },
      { vital_type: "heart_rate", value: v.heart_rate, unit: "bpm" },
    ],
  },
  {
    key: "temperature", label: "Body Temperature", measure: "temperature", icon: Thermometer, color: "#EA580C",
    instruction: "Hold the sensor to the forehead.", wave: false,
    summary: (v) => [`${v.temperature}`, "°C", ""],
    readings: (v) => [{ vital_type: "temperature", value: v.temperature, unit: "°C" }],
  },
  {
    key: "ecg", label: "ECG", measure: "ecg", icon: HeartPulse, color: "#7C3AED",
    instruction: "Hold both electrodes for ~30 seconds.", wave: true,
    summary: (v) => [`${v.heart_rate}`, "bpm", v.rhythm || "Normal sinus"],
    note: (v) => `ECG capture · HR ${v.heart_rate} bpm · HRV ${v.hrv} ms · ${v.rhythm || "normal sinus rhythm"}`,
    readings: (v) => [{ vital_type: "heart_rate", value: v.heart_rate, unit: "bpm" }],
  },
  {
    key: "glucose", label: "Blood Glucose", measure: "glucose", icon: Droplet, color: "#0891B2",
    instruction: "Insert a strip and apply the blood sample.", wave: false,
    summary: (v) => [`${v.blood_glucose}`, "mmol/L", ""],
    readings: (v) => [{ vital_type: "blood_glucose", value: v.blood_glucose, unit: "mmol/L" }],
  },
  {
    key: "cholesterol", label: "Cholesterol", measure: "cholesterol", icon: FlaskConical, color: "#CA8A04",
    instruction: "Insert a strip and apply the blood sample.", wave: false,
    summary: (v) => [`${v.cholesterol}`, "mmol/L", ""],
    readings: (v) => [{ vital_type: "cholesterol", value: v.cholesterol, unit: "mmol/L" }],
  },
  {
    key: "uric_acid", label: "Uric Acid", measure: "uric_acid", icon: FlaskConical, color: "#9333EA",
    instruction: "Insert a strip and apply the blood sample.", wave: false,
    summary: (v) => [`${v.uric_acid}`, "µmol/L", ""],
    readings: (v) => [{ vital_type: "uric_acid", value: v.uric_acid, unit: "µmol/L" }],
  },
];

/* ============================================================================
 * Waveform — lightweight streaming sparkline
 * ==========================================================================*/
function Waveform({ points, color }) {
  if (!points?.length) return null;
  const w = 320, h = 64;
  const path = points
    .map((p, i) => `${(i / (points.length - 1)) * w},${h - p * h}`)
    .join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-16" preserveAspectRatio="none" data-testid="iot-waveform">
      <polyline points={path} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

/* ============================================================================
 * Per-vital capture window
 * ==========================================================================*/
function VitalCaptureWindow({ vital, patientId, device, open, onOpenChange, onSubmitted }) {
  // phases: idle | measuring | result | error
  const [phase, setPhase] = useState("idle");
  const [wave, setWave] = useState([]);
  const [result, setResult] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const cancelRef = useRef(null);

  const reset = useCallback(() => {
    if (cancelRef.current) { cancelRef.current(); cancelRef.current = null; }
    setPhase("idle"); setWave([]); setResult(null); setErrMsg(""); setSaving(false);
  }, []);

  useEffect(() => { if (!open) reset(); }, [open, reset]);
  useEffect(() => () => { if (cancelRef.current) cancelRef.current(); }, []);

  function begin() {
    setPhase("measuring"); setWave([]); setResult(null); setErrMsg("");
    cancelRef.current = startMeasure(vital.measure, {
      onWave: (pts) => setWave(pts),
      onResult: (values) => { cancelRef.current = null; setResult(values); setPhase("result"); },
      onError: (m) => { cancelRef.current = null; setErrMsg(m); setPhase("error"); },
    });
  }

  async function save() {
    setSaving(true);
    try {
      const readings = vital.readings(result);
      const note = vital.note ? vital.note(result) : null;
      const r = await api.post(`/rpm/patients/${patientId}/manual-vitals`, {
        readings: readings.map((x) => ({ ...x, notes: note })),
        source: "iot_device",
        device_id: device?.deviceId,
        device_name: device?.name,
      });
      const alertsN = r.data.alerts_created?.length || 0;
      if (alertsN) toast.success(`${vital.label} saved · ${alertsN} alert${alertsN === 1 ? "" : "s"} fired`);
      else toast.success(`${vital.label} reading saved`);
      onSubmitted?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  }

  const Icon = vital.icon;
  const summary = result ? vital.summary(result) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={`iot-capture-${vital.key}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg" style={{ background: `${vital.color}1A`, color: vital.color }}>
              <Icon className="w-4 h-4" />
            </span>
            Capture · {vital.label}
          </DialogTitle>
        </DialogHeader>

        <div className="py-2 space-y-4">
          {/* IDLE */}
          {phase === "idle" && (
            <div className="text-center py-6 space-y-4" data-testid={`iot-capture-idle-${vital.key}`}>
              <span className="inline-flex items-center justify-center w-16 h-16 rounded-full" style={{ background: `${vital.color}14`, color: vital.color }}>
                <Icon className="w-8 h-8" />
              </span>
              <p className="text-sm text-[#475569] px-4">{vital.instruction}</p>
              <Button onClick={begin} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid={`iot-start-${vital.key}`}>
                Start measurement
              </Button>
            </div>
          )}

          {/* MEASURING */}
          {phase === "measuring" && (
            <div className="text-center py-6 space-y-4" data-testid={`iot-capture-measuring-${vital.key}`}>
              <Loader2 className="w-8 h-8 mx-auto animate-spin" style={{ color: vital.color }} />
              <p className="text-sm font-semibold text-[#1F2937]">Measuring…</p>
              <p className="text-xs text-[#6B7280] px-4">{vital.instruction} Keep the device steady.</p>
              {vital.wave && (
                <div className="rounded-lg border border-[#E5E7EB] bg-[#FAFBFC] p-2">
                  <Waveform points={wave} color={vital.color} />
                </div>
              )}
            </div>
          )}

          {/* RESULT */}
          {phase === "result" && summary && (
            <div className="text-center py-4 space-y-4" data-testid={`iot-capture-result-${vital.key}`}>
              <CheckCircle2 className="w-8 h-8 mx-auto text-[#10B981]" />
              <div>
                <div className="flex items-end justify-center gap-1">
                  <span className="text-4xl font-bold" style={{ color: vital.color }} data-testid={`iot-result-value-${vital.key}`}>{summary[0]}</span>
                  <span className="text-sm text-[#6B7280] mb-1">{summary[1]}</span>
                </div>
                {summary[2] && <div className="text-sm text-[#475569] mt-1">{summary[2]}</div>}
              </div>
              <div className="flex items-center justify-center gap-2 pt-2">
                <Button variant="outline" onClick={begin} className="border-[#E5E7EB]" data-testid={`iot-retake-${vital.key}`}>
                  <RefreshCw className="w-4 h-4 mr-1.5" /> Re-take
                </Button>
                <Button onClick={save} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid={`iot-save-${vital.key}`}>
                  <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save reading"}
                </Button>
              </div>
            </div>
          )}

          {/* ERROR */}
          {phase === "error" && (
            <div className="text-center py-6 space-y-4" data-testid={`iot-capture-error-${vital.key}`}>
              <XCircle className="w-8 h-8 mx-auto text-[#DC2626]" />
              <p className="text-sm text-[#991B1B]">{errMsg || "Measurement failed."}</p>
              <Button variant="outline" onClick={begin} className="border-[#E5E7EB]" data-testid={`iot-retry-${vital.key}`}>
                <RefreshCw className="w-4 h-4 mr-1.5" /> Try again
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================================
 * Main entry — Connect device → pick a vital → per-vital capture window
 * ==========================================================================*/
export default function IotDeviceDialog({ patientId, open, onOpenChange, onSubmitted }) {
  const mode = bridgeMode();
  // conn: idle | scanning | connecting | connected
  const [conn, setConn] = useState("idle");
  const [devices, setDevices] = useState([]);
  const [device, setDevice] = useState(null);
  const [activeVital, setActiveVital] = useState(null);
  const stopScanRef = useRef(null);

  const stopScan = useCallback(() => {
    if (stopScanRef.current) { stopScanRef.current(); stopScanRef.current = null; }
  }, []);

  const beginScan = useCallback(() => {
    setDevices([]); setConn("scanning");
    stopScan();
    stopScanRef.current = scanForDevices((d) =>
      setDevices((prev) => (prev.some((p) => p.id === d.id) ? prev : [...prev, d])),
    );
  }, [stopScan]);

  useEffect(() => {
    if (open && conn === "idle") beginScan();
    if (!open) {
      stopScan();
      setConn("idle"); setDevices([]); setDevice(null); setActiveVital(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => () => stopScan(), [stopScan]);

  async function pair(d) {
    stopScan();
    setConn("connecting");
    try {
      const info = await connectDevice(d.id, d.name);
      setDevice(info);
      setConn("connected");
      toast.success(`Connected to ${info.name}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setConn("idle");
      beginScan();
    }
  }

  function unpair() {
    disconnectDevice();
    setDevice(null);
    setConn("idle");
    beginScan();
  }

  const activeCfg = DEVICE_VITALS.find((v) => v.key === activeVital);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg" data-testid="iot-device-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bluetooth className="w-5 h-5 text-[#0B7E8C]" /> Capture Vitals via IoT Device
            </DialogTitle>
          </DialogHeader>

          {/* Mode banner */}
          <div
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs"
            style={mode === "native"
              ? { background: "#ECFDF5", color: "#065F46" }
              : { background: "#FEF3C7", color: "#92400E" }}
            data-testid="iot-mode-banner"
          >
            <Cpu className="w-3.5 h-3.5 shrink-0" />
            {mode === "native"
              ? "Live device bridge detected — readings come from the paired hardware."
              : "Simulator mode — no native bridge detected. Readings are demo values for testing this flow."}
          </div>

          {/* CONNECTION SECTION */}
          {conn !== "connected" && (
            <div className="py-1 space-y-3" data-testid="iot-connection-section">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-[#1F2937] flex items-center gap-1.5">
                  {conn === "scanning"
                    ? <><BluetoothSearching className="w-4 h-4 animate-pulse text-[#0B7E8C]" /> Scanning…</>
                    : conn === "connecting"
                    ? <><Loader2 className="w-4 h-4 animate-spin text-[#0B7E8C]" /> Connecting…</>
                    : <><Bluetooth className="w-4 h-4 text-[#0B7E8C]" /> Nearby devices</>}
                </span>
                <Button size="sm" variant="outline" onClick={beginScan} disabled={conn === "connecting"} className="h-7 border-[#E5E7EB]" data-testid="iot-rescan">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Rescan
                </Button>
              </div>

              <div className="space-y-2">
                {devices.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => pair(d)}
                    disabled={conn === "connecting"}
                    className="w-full flex items-center justify-between rounded-lg border border-[#E5E7EB] px-3 py-2.5 text-left hover:border-[#0B7E8C] hover:bg-[#F0FAFB] transition-colors disabled:opacity-50"
                    data-testid={`iot-device-${d.id}`}
                  >
                    <span className="flex items-center gap-2.5">
                      <Bluetooth className="w-4 h-4 text-[#0B7E8C]" />
                      <span>
                        <span className="block text-sm font-semibold text-[#1F2937]">{d.name}</span>
                        <span className="block text-[11px] text-[#94A3B8] font-mono">{d.id} · {d.rssi} dBm</span>
                      </span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
                  </button>
                ))}
                {devices.length === 0 && (
                  <div className="text-center py-8 text-sm text-[#6B7280]" data-testid="iot-no-devices">
                    {conn === "scanning" ? "Searching for devices…" : "No devices found. Make sure the device is on and in range."}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* CONNECTED — vital tiles */}
          {conn === "connected" && device && (
            <div className="py-1 space-y-3" data-testid="iot-connected-section">
              <div className="flex items-center justify-between rounded-lg bg-[#ECFDF5] border border-[#A7F3D0] px-3 py-2.5">
                <span className="flex items-center gap-2.5">
                  <BluetoothConnected className="w-5 h-5 text-[#059669]" />
                  <span>
                    <span className="block text-sm font-semibold text-[#065F46]" data-testid="iot-connected-name">{device.name}</span>
                    <span className="block text-[11px] text-[#047857] flex items-center gap-1">
                      <BatteryFull className="w-3 h-3" /> {device.battery}% · FW {device.firmware}
                    </span>
                  </span>
                </span>
                <Button size="sm" variant="outline" onClick={unpair} className="h-7 border-[#A7F3D0] text-[#065F46]" data-testid="iot-disconnect">
                  Disconnect
                </Button>
              </div>

              <p className="text-xs text-[#6B7280]">Select a vital to capture. Each opens its own measurement window.</p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {DEVICE_VITALS.map((v) => {
                  const Icon = v.icon;
                  return (
                    <button
                      key={v.key}
                      onClick={() => setActiveVital(v.key)}
                      className="flex flex-col items-center gap-1.5 rounded-xl border border-[#E5E7EB] p-3 hover:border-[#0B7E8C] hover:shadow-sm transition-all"
                      data-testid={`iot-vital-tile-${v.key}`}
                    >
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg" style={{ background: `${v.color}14`, color: v.color }}>
                        <Icon className="w-5 h-5" />
                      </span>
                      <span className="text-xs font-semibold text-[#1F2937] text-center leading-tight">{v.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dedicated per-vital capture window */}
      {activeCfg && (
        <VitalCaptureWindow
          vital={activeCfg}
          patientId={patientId}
          device={device}
          open={!!activeVital}
          onOpenChange={(o) => { if (!o) setActiveVital(null); }}
          onSubmitted={onSubmitted}
        />
      )}
    </>
  );
}
